import { Link } from "react-router-dom";
import type { Location } from "@/types";

interface NearbyLocationsProps {
  locations: Location[];
  currentLocationId?: string;
  categoryName?: string;
}

export default function NearbyLocations({ locations, currentLocationId, categoryName }: NearbyLocationsProps) {
  const nearby = locations.filter((l) => l.id !== currentLocationId);
  if (nearby.length === 0) return null;

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-extrabold text-foreground-950">Nearby locations</h2>
          <p className="mt-1 text-sm text-foreground-600">
            {categoryName ? `Explore ${categoryName.toLowerCase()} and more in these districts.` : "Explore businesses in nearby districts."}
          </p>
        </div>
        <Link
          to="/locations"
          className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
        >
          All locations <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {nearby.map((l) => (
          <Link
            key={l.id}
            to={`/location/${l.slug}`}
            className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-100/50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
          >
            <span className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md">
              <img
                src={l.image}
                alt={`Businesses in ${l.name}`}
                title={`${l.name} business directory`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </span>
            <span className="min-w-0">
              <span className="block font-heading text-sm font-bold text-foreground-950 leading-snug">{l.name}</span>
              <span className="mt-0.5 block text-xs text-foreground-500">{l.district} district</span>
              <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-secondary-700">
                <i className="ri-store-3-line"></i> {l.businessCount.toLocaleString()} listings
              </span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}